<template>
  <section class="upcoming-ipos-page">
    <div class="upcoming-ipos-page__inner">
      <header class="ipo-page-header">
        <p>Mercados</p>
        <h1>Próximas IPOs</h1>
        <span>Descubre empresas que saldrán a bolsa próximamente y revisa las incorporaciones más recientes.</span>
      </header>

      <div v-if="!hasAccess" class="ipo-panel ipo-access-state">
        <span class="ipo-access-state__icon" aria-hidden="true">◇</span>
        <h2>Próximas IPOs no incluidas</h2>
        <p>Tu plan actual no incluye acceso a esta herramienta.</p>
        <router-link class="ipo-button ipo-button--primary" :to="{ name: 'Pricing' }">Ver planes</router-link>
      </div>

      <template v-else>
        <div class="ipo-panel ipo-filter-panel">
          <div>
            <h2>Explora el calendario de salidas</h2>
            <p>Filtra por empresa, ticker o por los países disponibles en tu plan.</p>
          </div>
          <UpcomingIposFilters
            :query.sync="filters.query"
            :country.sync="filters.country"
            :country-options="countryOptions"
            :loading="isLoading"
            @submit="search"
            @reset="resetFilters"
          />
        </div>

        <div class="ipo-tabs" role="tablist" aria-label="Periodo de las IPOs">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            type="button"
            role="tab"
            :aria-selected="String(view === tab.id)"
            :class="{ 'is-active': view === tab.id }"
            @click="selectView(tab.id)"
          >
            {{ tab.label }} <span>{{ tab.count }}</span>
          </button>
        </div>

        <aside class="ipo-notice">
          <span aria-hidden="true">i</span>
          <p>
            Las fechas futuras son estimadas y pueden cambiar. Estos valores no implican el cumplimiento completo de la metodología Eurekers.
          </p>
        </aside>

        <div class="ipo-panel ipo-results-panel" aria-live="polite">
          <div class="ipo-results-panel__header">
            <div>
              <h2>{{ view === 'upcoming' ? 'Próximas salidas' : 'Incorporaciones recientes' }}</h2>
              <p>{{ resultDescription }}</p>
            </div>
            <span v-if="controller.status === 'refreshing'" class="ipo-refreshing">Actualizando…</span>
          </div>

          <div v-if="controller.status === 'loading'" class="ipo-state">
            <span class="ipo-spinner" aria-hidden="true"></span>
            <strong>Cargando IPOs…</strong>
            <p>Estamos preparando las salidas disponibles para tu plan.</p>
          </div>

          <div v-else-if="controller.status === 'error'" class="ipo-state ipo-state--error">
            <strong>No pudimos cargar las IPOs</strong>
            <p>{{ errorMessage }}</p>
            <button class="ipo-button ipo-button--secondary" type="button" @click="load">Reintentar</button>
          </div>

          <div v-else-if="!visibleItems.length" class="ipo-state">
            <strong>No hay IPOs en esta vista</strong>
            <p>Prueba con otro país o elimina el texto de búsqueda.</p>
            <button class="ipo-button ipo-button--secondary" type="button" @click="resetFilters">Limpiar filtros</button>
          </div>

          <UpcomingIposTable
            v-else
            :items="visibleItems"
            :phase="view"
            :sort-field="sortField"
            :sort-direction="sortDirection"
            @sort="changeSort"
          />
        </div>
      </template>
    </div>
  </section>
</template>

<script>
import { mapGetters } from 'vuex';
import UpcomingIposFilters from '@/features/upcoming-ipos/components/UpcomingIposFilters.vue';
import UpcomingIposTable from '@/features/upcoming-ipos/components/UpcomingIposTable.vue';
import { UpcomingIposController } from '@/features/upcoming-ipos/services/upcomingIposController';
import {
  defaultIpoSortDirection,
  hasUpcomingIposAccess,
  normalizeIpoRouteQuery,
  serializeIpoRouteQuery,
  sortUpcomingIpos
} from '@/features/upcoming-ipos/models/upcomingIpos';

export default {
  name: 'UpcomingIposPage',
  components: { UpcomingIposFilters, UpcomingIposTable },
  metaInfo: {
    meta: [{
      name: 'description',
      content: 'Consulta próximas salidas a bolsa e IPOs recientes en Eurekers.'
    }]
  },
  data() {
    return {
      controller: new UpcomingIposController(),
      filters: { query: '', country: 'all' },
      appliedFilters: { query: '', country: 'all' },
      view: 'upcoming',
      sortField: 'date',
      sortDirection: 'asc',
      loadedFilterKey: ''
    };
  },
  computed: {
    ...mapGetters(['_g_UserFeatures']),
    hasAccess() { return hasUpcomingIposAccess(this._g_UserFeatures); },
    isLoading() { return this.controller.status === 'loading' || this.controller.status === 'refreshing'; },
    countryOptions() { return this.controller.meta.countryOptions; },
    tabs() {
      return [
        { id: 'upcoming', label: 'Próximas', count: this.controller.meta.counts.upcoming },
        { id: 'recent', label: 'Recientes', count: this.controller.meta.counts.recent }
      ];
    },
    visibleItems() {
      return sortUpcomingIpos(
        this.controller.items,
        this.view,
        this.sortField,
        this.sortDirection
      );
    },
    resultDescription() {
      const count = this.visibleItems.length;
      if (this.view === 'upcoming') {
        return `${count} ${count === 1 ? 'empresa prevista' : 'empresas previstas'} durante el próximo mes.`;
      }
      return `${count} ${count === 1 ? 'empresa incorporada' : 'empresas incorporadas'} durante los últimos seis meses.`;
    },
    errorMessage() {
      return this.controller.error && this.controller.error.message
        ? this.controller.error.message
        : 'Vuelve a intentarlo dentro de unos instantes.';
    }
  },
  watch: {
    '$route.fullPath'() {
      this.hydrateFromRoute();
      if (this.hasAccess && this.loadedFilterKey !== this.filterKey()) this.load();
    }
  },
  mounted() {
    this.hydrateFromRoute();
    if (this.hasAccess) this.load();
  },
  methods: {
    hydrateFromRoute() {
      const state = normalizeIpoRouteQuery(this.$route.query);
      this.filters.query = state.query;
      this.filters.country = state.country;
      this.appliedFilters.query = state.query;
      this.appliedFilters.country = state.country;
      this.view = state.view;
      this.sortField = state.sort;
      this.sortDirection = state.direction;
    },
    filterKey() { return `${this.appliedFilters.query.trim()}::${this.appliedFilters.country}`; },
    async load() {
      const key = this.filterKey();
      this.loadedFilterKey = key;
      try {
        await this.controller.load(this.appliedFilters);
      }
      catch (error) {
        // The controller exposes the recoverable error state to the template.
      }
    },
    syncRoute() {
      const query = serializeIpoRouteQuery({
        view: this.view,
        query: this.appliedFilters.query,
        country: this.appliedFilters.country,
        sort: this.sortField,
        direction: this.sortDirection
      });
      const location = { name: 'UpcomingIpos', query };
      const target = this.$router.resolve(location).route.fullPath;

      if (target === this.$route.fullPath) {
        this.load();
        return;
      }
      this.$router.replace(location).catch(() => {});
    },
    search() {
      this.appliedFilters.query = this.filters.query.trim();
      this.appliedFilters.country = this.filters.country;
      this.syncRoute();
    },
    resetFilters() {
      this.filters.query = '';
      this.filters.country = 'all';
      this.appliedFilters.query = '';
      this.appliedFilters.country = 'all';
      this.syncRoute();
    },
    selectView(view) {
      if (this.view === view) return;
      this.view = view;
      if (this.sortField === 'date') this.sortDirection = defaultIpoSortDirection(view);
      this.syncRoute();
    },
    changeSort(field) {
      if (this.sortField === field) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      }
      else {
        this.sortField = field;
        this.sortDirection = field === 'date' ? defaultIpoSortDirection(this.view) : 'asc';
      }
      this.syncRoute();
    }
  }
};
</script>

<style lang="scss" scoped>
.upcoming-ipos-page {
  height: 100%;
  overflow-y: auto;
  background:
    radial-gradient(circle at 80% 0, color-mix(in srgb, var(--modern-accent) 8%, transparent), transparent 32rem),
    var(--modern-bg);
  color: var(--modern-text);
  font-family: 'Open Sans', sans-serif;
}
.upcoming-ipos-page__inner { width: min(1180px, calc(100% - 40px)); margin: 0 auto; padding: 34px 0 56px; }
.ipo-page-header { margin-bottom: 24px; }
.ipo-page-header p { margin: 0 0 8px; color: var(--modern-accent); font-size: 12px; font-weight: 800; letter-spacing: .12em; text-transform: uppercase; }
.ipo-page-header h1 { margin: 0; font-size: clamp(28px, 4vw, 42px); line-height: 1.15; letter-spacing: -.035em; }
.ipo-page-header > span { display: block; max-width: 720px; margin-top: 10px; color: var(--modern-muted); font-size: 15px; line-height: 1.6; }
.ipo-panel { border: 1px solid var(--modern-border); border-radius: 18px; background: var(--modern-surface); box-shadow: 0 10px 30px rgba(15, 23, 42, .055); }
.ipo-filter-panel { display: grid; grid-template-columns: minmax(220px, .7fr) minmax(520px, 1.3fr); gap: 32px; align-items: end; padding: 22px; }
.ipo-filter-panel h2, .ipo-results-panel h2, .ipo-access-state h2 { margin: 0; font-size: 18px; }
.ipo-filter-panel p, .ipo-results-panel p, .ipo-access-state p { margin: 7px 0 0; color: var(--modern-muted); font-size: 13px; line-height: 1.5; }
.ipo-button { display: inline-flex; align-items: center; justify-content: center; min-height: 42px; box-sizing: border-box; padding: 0 15px; border: 1px solid transparent; border-radius: 10px; cursor: pointer; font: inherit; font-size: 13px; font-weight: 700; text-decoration: none; }
.ipo-button:disabled { cursor: wait; opacity: .65; }
.ipo-button--primary { background: var(--modern-accent); color: #fff; }
.ipo-button--secondary { border-color: var(--modern-border); background: var(--modern-surface); color: var(--modern-text); }
.ipo-button:focus-visible, .ipo-tabs button:focus-visible, .ipo-table button:focus-visible, .ipo-table a:focus-visible { outline: 3px solid var(--modern-accent-soft); outline-offset: 2px; }
.ipo-tabs { display: flex; gap: 8px; margin: 22px 0 14px; }
.ipo-tabs button { display: flex; align-items: center; gap: 9px; padding: 10px 16px; border: 1px solid var(--modern-border); border-radius: 999px; background: var(--modern-surface); color: var(--modern-muted); cursor: pointer; font: inherit; font-size: 13px; font-weight: 700; }
.ipo-tabs button span { display: grid; place-items: center; min-width: 22px; height: 22px; padding: 0 5px; border-radius: 11px; background: var(--modern-bg); font-size: 11px; }
.ipo-tabs button.is-active { border-color: var(--modern-accent); background: var(--modern-accent-soft); color: var(--modern-accent); }
.ipo-notice { display: flex; gap: 11px; align-items: flex-start; margin-bottom: 14px; padding: 12px 15px; border: 1px solid color-mix(in srgb, var(--modern-accent) 30%, var(--modern-border)); border-radius: 12px; background: var(--modern-accent-soft); color: var(--modern-muted); }
.ipo-notice > span { display: grid; place-items: center; width: 20px; height: 20px; flex: 0 0 auto; border-radius: 50%; background: var(--modern-accent); color: #fff; font-size: 12px; font-weight: 800; }
.ipo-notice p { margin: 0; font-size: 12px; line-height: 1.55; }
.ipo-results-panel { overflow: hidden; }
.ipo-results-panel__header { display: flex; justify-content: space-between; gap: 16px; align-items: center; padding: 20px 22px; border-bottom: 1px solid var(--modern-border); }
.ipo-refreshing { color: var(--modern-accent); font-size: 12px; font-weight: 700; }
.ipo-state { display: grid; justify-items: center; gap: 7px; padding: 58px 24px; text-align: center; }
.ipo-state strong { font-size: 16px; }
.ipo-state p { max-width: 460px; margin-bottom: 10px; }
.ipo-state--error strong { color: #c3364f; }
.ipo-spinner { width: 25px; height: 25px; margin-bottom: 5px; border: 3px solid var(--modern-border); border-top-color: var(--modern-accent); border-radius: 50%; animation: ipo-spin .8s linear infinite; }
.ipo-access-state { display: grid; justify-items: center; max-width: 560px; margin: 70px auto 0; padding: 42px; text-align: center; }
.ipo-access-state__icon { display: grid; place-items: center; width: 52px; height: 52px; margin-bottom: 8px; border-radius: 16px; background: var(--modern-accent-soft); color: var(--modern-accent); font-size: 29px; }
.ipo-access-state .ipo-button { margin-top: 15px; }
@keyframes ipo-spin { to { transform: rotate(360deg); } }
@media (max-width: 900px) {
  .upcoming-ipos-page__inner { width: min(100% - 28px, 760px); padding-top: 24px; }
  .ipo-filter-panel { grid-template-columns: 1fr; gap: 18px; }
}
@media (max-width: 560px) {
  .ipo-results-panel__header { align-items: flex-start; }
}
@supports not (color: color-mix(in srgb, white 50%, black)) {
  .upcoming-ipos-page { background: var(--modern-bg); }
  .ipo-notice { border-color: var(--modern-border); }
}
</style>
